import React, { useCallback, useMemo } from "react";
import { Button, DownloadIcon, Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import LicenseDecisionModal, {
  LicenseDecisionValue,
} from "~/pages/LicenseManagement/LicenseDecisionModal";
import LicenseDecisionResultModal from "~/pages/LicenseManagement/LicenseDecisionResultModal";
import LicenseRecalculateModal from "~/pages/LicenseManagement/LicenseRecalculateModal";
import LicenseRequestModal from "~/pages/LicenseManagement/LicenseRequestModal";
import CalculateNeedIcon from "~/pages/LicenseManagement/parts/CalculateNeedIcon";
import {
  ILicenseRecalculateParams,
  ILicenseRequestRow,
  ILicenseRequestTransitionItem,
  useCalculateLicenseNeed,
  useGetLicenseRequestsList,
  useSetLicenseRequest,
} from "~/entity/Licenses";
import { LicenseRequestsService } from "~/entity/Licenses/api/licenseRequestsService";
import { useCurrentDealerInfo } from "~/hooks/useCurrentDealerInfo";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { LICENSE_ADMIN_OPTIONS } from "~/pages/LicenseManagement/adminOptions";
import { getAdminLicenseActionsColumns } from "~/pages/LicenseManagement/model/adminTableAdapter";
import {
  ILicenseManagementUrlParams,
  parseLicenseAdminFilterFromUrl,
} from "~/pages/LicenseManagement/model/licenseManagementUrlParams";
import {
  mapFormToCreatePayload,
  mapFormToUpdatePayload,
} from "~/pages/LicenseManagement/model/mapFormToRequest";
import {
  mapLicenseFilterToQuery,
  needsMergedFinalStatuses,
} from "~/pages/LicenseManagement/model/mapLicenseFilterToQuery";
import { ILicenseRequestForm } from "~/pages/LicenseManagement/model/types";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { sortItems, TableV2, useURLTableSort } from "~/shared/ui/TableV2";

import s from "~/pages/LicenseManagement/LicenseManagement.less";

interface AdminLicenseManagementViewProps {
  isAdmin: boolean;
  canSelectDealer: boolean;
}

const SORTABLE_COLUMNS: string[] = [...LICENSE_ADMIN_OPTIONS];

const AdminLicenseManagementView = ({
  isAdmin,
  canSelectDealer,
}: AdminLicenseManagementViewProps) => {
  const { t } = useTranslation();
  const [dealer] = useCurrentDealerInfo();
  const setParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<ILicenseManagementUrlParams>();
  const { page, count = 25, sortField, sortType: sortTypeRaw } = urlParams;

  const { sortType: normalizedSortType, onSortChange } = useURLTableSort({
    sortField: sortField ? String(sortField) : undefined,
    sortType: sortTypeRaw ? String(sortTypeRaw) : undefined,
    sortableColumns: SORTABLE_COLUMNS,
    setParams,
  });

  const tableSortField =
    sortField && SORTABLE_COLUMNS.includes(String(sortField))
      ? String(sortField)
      : undefined;

  const formFilter = useMemo(
    () => parseLicenseAdminFilterFromUrl(urlParams),
    [urlParams],
  );

  const pageNumber = Number(page) || 0;
  const pageSize = Number(count) || 25;
  const currentDealerId = useCurrentDealerId();
  const dealerId = dealer?.id || currentDealerId;
  const queryParams = useMemo(
    () => mapLicenseFilterToQuery(pageNumber, pageSize, formFilter),
    [pageNumber, pageSize, formFilter],
  );

  const { requests, total, isLoading } = useGetLicenseRequestsList({
    params: queryParams,
    isAdmin,
    dealerId,
    mergeFinalStatuses: needsMergedFinalStatuses(formFilter),
  });

  const {
    createLicenseRequest,
    updateLicenseRequest,
    sendLicenseRequest,
    approveLicenseRequest,
    rejectLicenseRequest,
    approveAllCalculation,
  } = useSetLicenseRequest();
  const calculateLicenseNeed = useCalculateLicenseNeed();
  const { notifyWarning } = useNotify();
  const { hasPermission } = usePermissions();
  const canRecalculate = hasPermission(
    "employeeAccounting.license-requests.calculate.put",
  );
  const canMakeDecision = hasPermission(
    "employeeAccounting.license-requests.id.put",
  );
  const canExport = hasPermission(
    "employeeAccounting.license-requests.export.get",
  );

  const sorted = useMemo(
    () =>
      sortItems(requests, tableSortField, normalizedSortType, (item, field) => {
        if (field === "status") {
          return t(`licenseManagement.statuses.${item.status}`);
        }

        if (field === "actionType") {
          return t(`licenseManagement.actionTypes.${item.actionType}`);
        }

        if (field === "decision") {
          return item.decision
            ? t(`licenseManagement.decisions.${item.decision}`)
            : "";
        }

        return item[field as keyof ILicenseRequestRow];
      }),
    [requests, tableSortField, normalizedSortType, t],
  );

  const handleMakeDecision = useCallback(
    (record: ILicenseRequestRow) => {
      setParams({
        openModal:
          record.actionType === "baseIssue" ? "DecisionResult" : "Decision",
        currentId: record.id,
      });
    },
    [setParams],
  );

  const handleOpenDecisionResult = useCallback(
    (record: ILicenseRequestRow) => {
      setParams({ openModal: "DecisionResult", currentId: record.id });
    },
    [setParams],
  );

  const handleSendDraft = useCallback(
    (record: ILicenseRequestRow) => {
      sendLicenseRequest.mutate({ id: record.id });
    },
    [sendLicenseRequest],
  );

  const handleEditDraft = useCallback(
    (record: ILicenseRequestRow) => {
      setParams({ openModal: "CreateRequest", currentId: record.id });
    },
    [setParams],
  );

  const columns = useMemo(
    () =>
      getAdminLicenseActionsColumns({
        t,
        isAdmin,
        canMakeDecision,
        onMakeDecision: handleMakeDecision,
        onOpenDecisionResult: handleOpenDecisionResult,
        onSendDraft: handleSendDraft,
        onEditDraft: handleEditDraft,
        sortField: tableSortField,
        sortType: normalizedSortType,
        sortableColumns: SORTABLE_COLUMNS,
      }),
    [
      t,
      isAdmin,
      canMakeDecision,
      handleMakeDecision,
      handleOpenDecisionResult,
      handleSendDraft,
      handleEditDraft,
      tableSortField,
      normalizedSortType,
    ],
  );

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  const handleCreate = async (data: ILicenseRequestForm, send: boolean) => {
    const partnerValue = data.partner?.value;
    const nextDealerId = partnerValue
      ? undefined
      : data.dealer?.value || dealer?.id;

    if (!nextDealerId && !partnerValue) {
      notifyWarning(t("licenseManagement.notification.errorNoDealer"));
      return;
    }

    const owner = {
      dealerId: nextDealerId ? String(nextDealerId) : undefined,
      organizationId: partnerValue,
    };

    const editId = urlParams.currentId || null;

    if (editId) {
      await updateLicenseRequest.mutateAsync({
        id: editId,
        data: mapFormToUpdatePayload(data.positions, owner, send),
        send,
      });
      return;
    }

    await createLicenseRequest.mutateAsync({
      data: mapFormToCreatePayload(data.positions, owner, send),
      send,
    });
  };

  const handleConfirmDecision = (
    actionId: string,
    decision: LicenseDecisionValue,
    comment?: string,
    items?: ILicenseRequestTransitionItem[],
  ) => {
    if (decision === "approve") {
      approveLicenseRequest.mutate({ id: actionId, data: { comment, items } });
      return;
    }

    rejectLicenseRequest.mutate({ id: actionId, data: { comment } });
  };

  const handleExportExcel = () => {
    const exportParams = isAdmin
      ? queryParams
      : { ...queryParams, dealerId: Number(dealerId) };

    document.location.href = LicenseRequestsService.GET.exportUrl(exportParams);
  };

  const handleCalculateNeed = () => {
    calculateLicenseNeed.start();
  };

  const handleRecalculate = (params: ILicenseRecalculateParams) => {
    calculateLicenseNeed.start(params);
  };

  const openRecalculateModal = () =>
    setParams({ openModal: "Recalculate", currentId: undefined });

  const handleApproveAll = () => {
    approveAllCalculation.mutate({
      year: formFilter.year ? Number(formFilter.year) : undefined,
      dealerIds: formFilter.dealerId
        ? [Number(formFilter.dealerId)]
        : undefined,
    });
  };

  const isCalculating =
    calculateLicenseNeed.isStarting ||
    calculateLicenseNeed.job?.status === "RUNNING";

  return (
    <>
      <div className={s.downloadToolbar}>
        {canExport && (
          <Button
            bold
            color="secondary"
            variant="filled"
            fontSize="10"
            size="small"
            radius={30}
            icon={<DownloadIcon size={12} />}
            onClick={handleExportExcel}
          >
            {t("licenseManagement.downloadExcel")}
          </Button>
        )}
        <div className={s.toolbarGroup}>
          <Button
            bold
            color="secondary"
            variant="filled"
            fontSize="10"
            size="small"
            radius={30}
            icon={<CalculateNeedIcon size={12} />}
            loading={isCalculating}
            onClick={handleCalculateNeed}
          >
            {t("licenseManagement.calculateNeed")}
          </Button>
          {canRecalculate && (
            <>
              <Button
                bold
                color="secondary"
                variant="filled"
                fontSize="10"
                size="small"
                radius={30}
                icon={<CalculateNeedIcon size={12} />}
                loading={isCalculating}
                onClick={openRecalculateModal}
              >
                {t("licenseManagement.recalculate")}
              </Button>
              <Button
                bold
                color="secondary"
                variant="filled"
                fontSize="10"
                size="small"
                radius={30}
                loading={approveAllCalculation.isLoading}
                onClick={handleApproveAll}
              >
                {t("licenseManagement.approveAll")}
              </Button>
            </>
          )}
        </div>
      </div>

      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={sorted}
        rowKey="id"
        scroll={{ x: "max-content" }}
        sortableColumns={SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        loading={isLoading}
        onRow={(record) => ({
          onClick: () => handleOpenDecisionResult(record),
          style: { cursor: "pointer" },
        })}
      />

      <Pagination
        count={pageSize}
        page={pageNumber + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
        selectFrom={[25, 50, 100]}
      />

      {isAdmin && (
        <LicenseDecisionModal
          requests={requests}
          onConfirm={handleConfirmDecision}
        />
      )}
      <LicenseDecisionResultModal requests={requests} />
      <LicenseRequestModal
        onSubmit={handleCreate}
        canSelectDealer={canSelectDealer}
      />
      <LicenseRecalculateModal onSubmit={handleRecalculate} />
    </>
  );
};

export default AdminLicenseManagementView;
